import { useEffect, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Share2, Download, Loader2, AlertTriangle, Triangle as TriangleIcon } from 'lucide-react';
import { toast } from 'sonner';
import ModalCloseButton from './ModalCloseButton';

// TriangleShareModal — branded 540×N PNG card for the Dividend Triangle.
//
// Same html-to-image capture flow as TimeMachineShareModal and DripShareModal.
// Renders the radar as hand-rolled SVG (NOT Recharts) so html-to-image
// captures it cleanly — Recharts' ResponsiveContainer + foreignObject mix
// historically misbehaves under toPng's clone step.
//
// Props:
//   open      — boolean
//   onClose   — () => void
//   results   — array of triangle payloads (from /triangle or /triangle/compare).
//               Works in both modes: 1 ticker (solo card) or 2-5 tickers
//               (compare card). Each entry needs `ticker`, `radar`,
//               `triangle_score`, `revenue`, `eps`, `dividend`, `trap_flag`.
//   mode      — 'solo' | 'compare' (controls heading copy and filename)

const PALETTE = [
  { stroke: '#10b981', fill: 'rgba(16, 185, 129, 0.28)' },  // emerald
  { stroke: '#a78bfa', fill: 'rgba(167, 139, 250, 0.26)' }, // violet
  { stroke: '#fbbf24', fill: 'rgba(251, 191, 36, 0.26)' },  // amber
  { stroke: '#38bdf8', fill: 'rgba(56, 189, 248, 0.26)' },  // sky
  { stroke: '#f472b6', fill: 'rgba(244, 114, 182, 0.26)' }, // pink
];

const RADAR_SIZE = 360;        // SVG viewport in card pixels
const RADAR_CENTER = RADAR_SIZE / 2;
const RADAR_R = 130;           // outer radius

// 3 axes 120° apart: revenue at top, eps bottom-right, dividend bottom-left.
const AXIS_ANGLES = {
  revenue: -Math.PI / 2,                      // straight up
  eps: -Math.PI / 2 + (2 * Math.PI) / 3,      // 120° clockwise
  dividend: -Math.PI / 2 - (2 * Math.PI) / 3, // 240° clockwise
};

function pointAt(axis, value0to100) {
  const r = (value0to100 / 100) * RADAR_R;
  const a = AXIS_ANGLES[axis];
  return [
    RADAR_CENTER + r * Math.cos(a),
    RADAR_CENTER + r * Math.sin(a),
  ];
}

function pathFor(radar) {
  if (!radar) return '';
  const pts = ['revenue', 'eps', 'dividend'].map(ax => pointAt(ax, radar[ax] ?? 0));
  return `M ${pts[0][0]},${pts[0][1]} L ${pts[1][0]},${pts[1][1]} L ${pts[2][0]},${pts[2][1]} Z`;
}

function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  return `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`;
}

function RadarSVG({ results }) {
  // Concentric grid: 25/50/75/100
  const rings = [25, 50, 75, 100];

  // Axis label position (just outside outer ring)
  const labelR = RADAR_R + 22;
  const labels = ['revenue', 'eps', 'dividend'].map(ax => ({
    ax,
    text: ax === 'revenue' ? 'REVENUE' : ax === 'eps' ? 'EPS' : 'DIVIDEND',
    x: RADAR_CENTER + labelR * Math.cos(AXIS_ANGLES[ax]),
    y: RADAR_CENTER + labelR * Math.sin(AXIS_ANGLES[ax]),
  }));

  return (
    <svg
      width={RADAR_SIZE}
      height={RADAR_SIZE}
      viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
      style={{ display: 'block', margin: '0 auto' }}
    >
      {/* Concentric triangular rings */}
      {rings.map((r) => (
        <path
          key={r}
          d={pathFor({ revenue: r, eps: r, dividend: r })}
          fill="none"
          stroke="rgba(148, 163, 184, 0.25)"
          strokeWidth={1}
        />
      ))}

      {/* Axis spokes */}
      {Object.keys(AXIS_ANGLES).map((ax) => {
        const [x, y] = pointAt(ax, 100);
        return (
          <line
            key={ax}
            x1={RADAR_CENTER}
            y1={RADAR_CENTER}
            x2={x}
            y2={y}
            stroke="rgba(148, 163, 184, 0.2)"
            strokeWidth={1}
          />
        );
      })}

      {/* One filled polygon per ticker */}
      {results.map((r, i) => {
        if (!r.radar) return null;
        const c = PALETTE[i % PALETTE.length];
        return (
          <g key={r.ticker}>
            <path d={pathFor(r.radar)} fill={c.fill} stroke={c.stroke} strokeWidth={2.5} />
          </g>
        );
      })}

      {/* Axis labels — last so they render on top */}
      {labels.map((l) => (
        <text
          key={l.ax}
          x={l.x}
          y={l.y}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          fontWeight={700}
          letterSpacing={1.5}
          fill="#cbd5e1"
        >
          {l.text}
        </text>
      ))}
    </svg>
  );
}

function ScoreColor(score) {
  if (score === null || score === undefined) return '#94a3b8';
  if (score >= 70) return '#34d399';
  if (score >= 50) return '#60a5fa';
  if (score >= 35) return '#fbbf24';
  return '#fb7185';
}

function CagrColor(c) {
  if (c === null || c === undefined) return '#94a3b8';
  if (c >= 5) return '#34d399';
  if (c >= 0) return '#fbbf24';
  return '#fb7185';
}

function ShareCard({ results, mode, cardRef }) {
  const isCompare = mode === 'compare';
  const title = isCompare
    ? `${results.map(r => r.ticker).join(' vs ')} · Dividend Triangle`
    : `${results[0]?.ticker || ''} · Dividend Triangle`;
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div
      ref={cardRef}
      data-testid="triangle-share-card"
      style={{
        width: '540px',
        background: 'linear-gradient(145deg, #0f172a 0%, #0b1220 60%, #020617 100%)',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid rgba(148, 163, 184, 0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TriangleIcon size={16} color="#a78bfa" />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            DiviDrip · Dividend Triangle
          </span>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, letterSpacing: '-0.01em' }}>
          {title}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
          5-year compound growth · Revenue / EPS / Dividend · {today}
        </div>
      </div>

      {/* Radar */}
      <div style={{ padding: '18px 24px 8px', background: 'rgba(15, 23, 42, 0.4)' }}>
        <RadarSVG results={results} />

        {/* Color legend below chart (compare mode only) */}
        {isCompare && (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 6 }}>
            {results.map((r, i) => {
              const c = PALETTE[i % PALETTE.length];
              return (
                <div key={r.ticker} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 5, background: c.stroke, display: 'inline-block' }} />
                  <span style={{ color: c.stroke }}>{r.ticker}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Scores table */}
      <div style={{ padding: '12px 24px 16px' }}>
        {/* Solo mode: big centered score */}
        {!isCompare && results[0] && (
          <div style={{ textAlign: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', letterSpacing: '0.18em', fontWeight: 700, textTransform: 'uppercase' }}>
              Triangle Score
            </div>
            <div style={{ fontSize: 48, fontWeight: 800, fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace', color: ScoreColor(results[0].triangle_score), lineHeight: 1.05 }}>
              {results[0].triangle_score ?? '—'}<span style={{ fontSize: 22, color: '#64748b' }}>/100</span>
            </div>
          </div>
        )}

        {/* Per-ticker mini-row */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {results.map((r, i) => {
            const c = PALETTE[i % PALETTE.length];
            return (
              <div
                key={r.ticker}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr 1fr 1fr 60px',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  background: 'rgba(30, 41, 59, 0.45)',
                  border: '1px solid rgba(148, 163, 184, 0.12)',
                  borderRadius: 10,
                  fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: c.stroke, display: 'inline-block' }} />
                  <span style={{ color: c.stroke }}>{r.ticker}</span>
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: CagrColor(r.revenue?.cagr_pct) }}>
                  R: {fmtPct(r.revenue?.cagr_pct)}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: CagrColor(r.eps?.cagr_pct) }}>
                  E: {fmtPct(r.eps?.cagr_pct)}
                </div>
                <div style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', color: CagrColor(r.dividend?.cagr_pct) }}>
                  D: {fmtPct(r.dividend?.cagr_pct)}
                </div>
                <div style={{ textAlign: 'right', fontWeight: 800, fontFamily: 'ui-monospace, monospace', color: ScoreColor(r.triangle_score) }}>
                  {r.triangle_score ?? '—'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trap callouts (compact, just the tickers that flagged) */}
        {results.some(r => r.trap_flag) && (
          <div style={{
            marginTop: 10,
            padding: '8px 10px',
            background: 'rgba(244, 63, 94, 0.10)',
            border: '1px solid rgba(244, 63, 94, 0.35)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}>
            <AlertTriangle size={14} color="#fb7185" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 11, color: '#fecdd3', lineHeight: 1.5 }}>
              <span style={{ fontWeight: 800 }}>Potential dividend trap:</span>{' '}
              {results.filter(r => r.trap_flag).map(r => r.ticker).join(', ')}
              {' '}— dividend growth is outpacing the underlying business.
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '12px 24px 16px',
        borderTop: '1px solid rgba(148, 163, 184, 0.12)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          Educational only · not investment advice
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src="/dividrip-icon.png"
            alt=""
            width={20}
            height={20}
            crossOrigin="anonymous"
            style={{ display: 'block' }}
          />
          <div style={{ fontSize: 12, fontWeight: 800, color: '#a78bfa', letterSpacing: '0.1em' }}>
            DiviDrip · twylightcrow.com
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TriangleShareModal({ open, onClose, results, mode = 'solo' }) {
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  // Preview-scale plumbing. The capture card is always rendered at its
  // true 540px capture width (so the downloaded PNG is pixel-perfect),
  // but the on-screen preview gets dynamically scaled to fit whatever
  // width the modal currently has — fixes phone preview where the
  // 540px card was getting cropped on both sides because the prior
  // hard-coded scale-[0.88] couldn't squeeze under ~400px viewports.
  const previewBoxRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [cardNaturalHeight, setCardNaturalHeight] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const recompute = () => {
      const box = previewBoxRef.current;
      const card = cardRef.current;
      if (!box || !card) return;
      const available = box.clientWidth; // already excludes box padding
      const scale = Math.min(1, available / 540);
      setPreviewScale(scale > 0 ? scale : 1);
      setCardNaturalHeight(card.scrollHeight);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (previewBoxRef.current) ro.observe(previewBoxRef.current);
    if (cardRef.current) ro.observe(cardRef.current);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [open, results, mode]);

  if (!open) return null;
  const safeResults = (results || []).filter(r => r && !r.error);
  if (safeResults.length === 0) return null;

  const filenameBase = mode === 'compare'
    ? `dividrip-triangle-compare-${safeResults.map(r => r.ticker.toLowerCase()).join('-')}`
    : `dividrip-triangle-${safeResults[0].ticker.toLowerCase()}`;

  const capture = async () => {
    if (!cardRef.current) throw new Error('card not mounted');
    const dataUrl = await toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#0b1220',
    });
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return { dataUrl, blob };
  };

  const triggerDownload = (dataUrl, filename) => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { blob, dataUrl } = await capture();
      const file = new File([blob], `${filenameBase}.png`, { type: 'image/png' });
      const shareText = mode === 'compare'
        ? `${safeResults.map(r => `${r.ticker} ${r.triangle_score}`).join(' · ')} · Dividend Triangle showdown`
        : `${safeResults[0].ticker} · Dividend Triangle ${safeResults[0].triangle_score}/100`;
      const canUseShare =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });
      if (canUseShare) {
        await navigator.share({
          files: [file],
          title: 'DiviDrip Dividend Triangle',
          text: shareText,
        });
        toast.success('Shared!');
      } else {
        triggerDownload(dataUrl, `${filenameBase}.png`);
        toast.success('Downloaded — share manually', {
          description: 'Your browser doesn\'t support native share for images. The file is in your Downloads.',
        });
      }
    } catch (err) {
      if (err && err.name !== 'AbortError') {
        toast.error('Unable to generate share image', { description: String(err.message || err) });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { dataUrl } = await capture();
      triggerDownload(dataUrl, `${filenameBase}.png`);
      toast.success('Saved to Downloads');
    } catch (err) {
      toast.error('Unable to generate PNG', { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      data-testid="triangle-share-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-[620px] max-h-[92vh] overflow-y-auto pb-24 sm:pb-4" data-testid="triangle-share-modal">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <TriangleIcon className="w-5 h-5 text-violet-500" />
            <h2 className="text-base font-bold">Share your Dividend Triangle</h2>
          </div>
          <ModalCloseButton onClick={onClose} testId="triangle-share-modal-close" />
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Only the card shown below is captured — nothing else on your screen.
          </p>

          <div
            ref={previewBoxRef}
            className="w-full rounded-lg border bg-muted/30 p-3 overflow-hidden flex justify-center"
            // The wrapper reserves the SCALED height of the card so the
            // modal doesn't get a giant empty gap below the preview when
            // the card is shrunk down on phone viewports.
            style={{ height: cardNaturalHeight > 0 ? cardNaturalHeight * previewScale + 24 : undefined }}
          >
            <div
              style={{
                transformOrigin: 'top center',
                transform: `scale(${previewScale})`,
                // Make the scaled child stop reserving its un-scaled 540px
                // worth of horizontal layout — without this the parent's
                // flex centering would still treat it as 540 wide.
                width: 540,
                flex: '0 0 540px',
              }}
            >
              <ShareCard results={safeResults} mode={mode} cardRef={cardRef} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleShare}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="triangle-share-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-lg border bg-background font-semibold text-sm hover:bg-muted transition-colors disabled:opacity-60"
              data-testid="triangle-share-download-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
